import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { generateAppRoutes, pathTo } from "./utils";
import { useIdleTimer } from "react-idle-timer";

import Button from "../../components/Button";
import LinkButton from "../../components/LinkButton";
import Modal from "../../components/ModalPopup";
import ButtonList from "../../components/ButtonList";
import { AuthService } from "../AuthProvider/authService";
import { fetchAuthSession } from "@aws-amplify/auth";

export const RouteContext = React.createContext({
  breadcrumbs: [],
  currentRoute: null,
  rootRoute: null,
  routes: [],
});

const RouteProvider = ({ children = null, value }) => {
  const location = useLocation();
  const [graceTimerId, setGraceTimerId] = useState(null);
  const [openRefreshModal, setOpenRefreshModal] = React.useState(false);

  const onIdle = () => {
    setOpenRefreshModal(true);
    const tId = setTimeout(
      () => {
        console.log("Logging out due to inactivity...");
        window.localStorage.clear();
        setOpenRefreshModal(false);
        window.location.href = "/login";
      },
      5 * 60 * 1000,
    ); // 5 minutes
    setGraceTimerId(tId);
  };

  const onActive = () => {
    // Don't auto-reset if modal is already open - user must explicitly choose
  };

  const idleTimer = useIdleTimer({
    timeout: 25 * 60 * 1000, // 25 minutes
    onIdle,
    onActive,
    debounce: 500,
    eventsThrottle: 200,
    startOnMount: true,
    startManually: false,
    stopOnIdle: false,
    crossTab: {
      emitOnAllTabs: true,
      type: "localStorage",
    },
  });

  // Monitor tab visibility for debugging
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log("Tab became HIDDEN at:", new Date().toISOString());
      } else {
        console.log("Tab became VISIBLE at:", new Date().toISOString());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [idleTimer]);

  const routes = generateAppRoutes(value);
  const currentRoute = routes.find((route) => route.path === location.pathname);
  const rootRoute = routes.find((route) => !route.parent);

  const breadcrumbs = currentRoute ? pathTo(currentRoute) : [];
  const context = { breadcrumbs, currentRoute, rootRoute, routes };

  const refreshConfirmModal = () => {
    return (
      <Modal open={openRefreshModal} onClose={() => setOpenRefreshModal(false)}>
        <h3>
          {" "}
          <LinkButton
            icon="overlay_icon"
            style={{
              fontSize: "5rem",
              float: "left",
              color: "grey",
              cursor: "none",
              outline: "none",
            }}
          />
          Your session is about to expire!
        </h3>
        <br />
        <div>You will be logged out in another 5 minutes.</div>
        <div>Do you want to stay signed in?</div>
        <br />
        <div style={{ marginTop: "1rem" }}>
          <ButtonList
            style={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Button primary onClick={() => refreshToken()}>
              Yes, Keep me signed in
            </Button>
            <Button
              onClick={() => {
                if (graceTimerId) {
                  clearTimeout(graceTimerId);
                  setGraceTimerId(null);
                }
                setOpenRefreshModal(false);
              }}
            >
              Close
            </Button>
          </ButtonList>
        </div>
      </Modal>
    );
  };

  const refreshToken = async () => {
    setOpenRefreshModal(false);
    if (graceTimerId) {
      clearTimeout(graceTimerId);
      setGraceTimerId(null);
    }

    if (idleTimer) {
      idleTimer.reset();
    }

    try {
      await fetchAuthSession();
      window.localStorage.setItem("SESSION_LOGIN_TIME", Date.now().toString());
    } catch (error) {
      console.error("Session refresh failed, logging out...", error);
      window.localStorage.clear();
      window.location.href = "/login";
    }
  };

  return (
    <RouteContext.Provider value={context}>
      {typeof children === "function" ? children(context) : children}
      {refreshConfirmModal()}
    </RouteContext.Provider>
  );
};

export default RouteProvider;
